import { useIntl } from '@klokhuis/core/Intl';
import { PageLayout } from '@klokhuis/core/PageLayout';
import { useMediaIsDesktop } from '@klokhuis/utilities/Media/useMediaIsDesktop';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PersonIcon from '@mui/icons-material/Person';
import SearchIcon from '@mui/icons-material/Search';
import SecurityIcon from '@mui/icons-material/Security';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  CardContent,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import styles from './HeaderHelpCenter.module.css';

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

interface Category {
  id: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const faqData: FAQItem[] = [
  {
    id: '1',
    category: 'account',
    question: 'How do I create an account on FindMyProps?',
    answer:
      'Creating an account is simple. Click on Sign Up, enter your email address, create a strong password, and verify your email. You can also sign up using your phone number and OTP verification.',
  },
  {
    id: '2',
    category: 'account',
    question: 'How do I reset my password?',
    answer:
      'Click on "Forgot Password" on the login page, enter your registered email, and we will send you a password reset link. Follow the link to create a new password.',
  },
  {
    id: '3',
    category: 'listings',
    question: 'How do I list a property on FindMyProps?',
    answer:
      'Log in to your account, click "List Property," fill in the property details, add photographs, set the price, and publish. Your listing will be visible to interested buyers immediately.',
  },
  {
    id: '4',
    category: 'listings',
    question: 'What information is required to list a property?',
    answer:
      'You need to provide property type, location, size, price, amenities, description, and clear photographs. For compliance, ensure RERA registration details are included if applicable.',
  },

  {
    id: '5',
    category: 'security',
    question: 'Is my personal information safe on FindMyProps?',
    answer:
      'Yes, we use SSL/TLS encryption, secure password hashing, and regular security audits to protect your data. Your information is never shared without consent except where necessary for property inquiries.',
  },
  {
    id: '6',
    category: 'security',
    question: 'How do I report a fraudulent listing?',
    answer:
      'Click the "Report" button on any listing page, select the reason for reporting (fraud, harassment, etc.), and provide details. Our team reviews all reports within 24 hours.',
  },
];

const categories: Category[] = [
  {
    id: 'account',
    title: 'Account & Login',
    description: 'Manage your account, reset passwords, and profile settings',
    icon: <PersonIcon sx={{ fontSize: 32, color: '#FF5A5F' }} />,
  },
  {
    id: 'listings',
    title: 'Listings & Properties',
    description: 'Create, edit, and manage your property listings',
    icon: <HelpOutlineIcon sx={{ fontSize: 32, color: '#FF5A5F' }} />,
  },

  {
    id: 'security',
    title: 'Safety & Security',
    description: 'Privacy, data protection, and fraud prevention',
    icon: <SecurityIcon sx={{ fontSize: 32, color: '#FF5A5F' }} />,
  },
];

export const HeaderHelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const isDesktop = useMediaIsDesktop();
  const { T } = useIntl();
  const filteredFAQ = faqData.filter(item => {
    const matchesSearch =
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory =
      !selectedCategory || item.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <PageLayout.SectionContainer>
      <div className={styles.container}>
        <Typography className={styles.header}> Help Center! </Typography>
      </div>

      <div>
        {/* Header */}

        {/* Search Bar */}
        <Box sx={{ mb: 6 }}>
          <TextField
            id="browse-faq"
            fullWidth
            placeholder="Search for help..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </Box>

        {/* Categories Grid */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Browse by Category
          </Typography>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr 1fr',
              },
              gap: 2,
            }}
          >
            {categories.map(category => (
              <Card
                key={category.id}
                onClick={() =>
                  setSelectedCategory(
                    selectedCategory === category.id ? null : category.id,
                  )
                }
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border:
                    selectedCategory === category.id
                      ? '2px solid #FF5A5F'
                      : '1px solid #e0e0e0',
                  backgroundColor:
                    selectedCategory === category.id ? '#fff5f5' : '#fff',
                  '&:hover': {
                    border: '2px solid #FF5A5F',
                    boxShadow: '0 4px 12px rgba(255, 90, 95, 0.15)',
                  },
                }}
              >
                <CardContent sx={{ textAlign: 'center' }}>
                  <Box sx={{ mb: 1 }}>{category.icon}</Box>
                  <Typography variant="pageHeroSubtitle">
                    {category.title}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {category.description}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>

        {/* FAQ Section */}
        <Box>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            {selectedCategory
              ? `${categories.find(c => c.id === selectedCategory)?.title} FAQs`
              : 'Frequently Asked Questions'}
          </Typography>

          {filteredFAQ.length > 0 ? (
            <Stack spacing={2}>
              {filteredFAQ.map(faq => (
                <Accordion key={faq.id}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {faq.question}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography color="textSecondary">{faq.answer}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Stack>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="textSecondary">
                No results found. Try different search terms or contact our
                support team.
              </Typography>
            </Box>
          )}
        </Box>

        {/* Support Contact */}
        <Box sx={{ mt: 8, p: 3, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Still need help?
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Can't find the answer you're looking for? Our support team is here
            to help.
          </Typography>
          <Typography variant="body2">
            <strong>Email:</strong> support@findmyprops.com
          </Typography>
          <Typography variant="body2">
            <strong>Response Time:</strong> Usually within 24 hours
          </Typography>
        </Box>
      </div>
    </PageLayout.SectionContainer>
  );
};